import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getMe, login, type AuthUser } from '../api/auth';
import { ApiError } from '../api/client';
import { getChannel, getChannelVoices, isSubscribed, playVoice } from '../api/channels';
import type { Channel, VoiceMessage } from '../api/types';
import { AudioPlayer } from '../components/AudioPlayer';
import { ChannelHeader } from '../components/ChannelHeader';
import { LoginGate, type GateReason } from '../components/LoginGate';
import { VoiceList } from '../components/VoiceList';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import './ChannelPage.css';

const GUEST_PLAY_LIMIT = 5;

function guestPlayKey(channelId: string) {
  return `secrettalk.web.guestplays.${channelId}`;
}

function getGuestPlays(channelId: string) {
  return Number(localStorage.getItem(guestPlayKey(channelId)) || '0');
}

function setGuestPlays(channelId: string, count: number) {
  localStorage.setItem(guestPlayKey(channelId), String(count));
}

function useChannelMeta(channel: Channel | null) {
  useEffect(() => {
    if (!channel) return;

    const previousTitle = document.title;
    const url = window.location.href;
    const title = `${channel.name} | SecretTalk`;
    const description = channel.description || `Listen to ${channel.creatorName} on SecretTalk.`;

    document.title = title;

    const upsertMeta = (selector: string, attribute: 'name' | 'property', key: string, value: string) => {
      let element = document.head.querySelector<HTMLMetaElement>(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', url);
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    return () => {
      document.title = previousTitle;
    };
  }, [channel]);
}

export function ChannelPage() {
  const { channelId } = useParams<{ channelId: string }>();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [voices, setVoices] = useState<VoiceMessage[]>([]);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [access, setAccess] = useState<'guest_preview' | 'member_locked' | 'owner' | 'partial' | 'subscribed'>('guest_preview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginReason, setLoginReason] = useState<GateReason>('login');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [guestPlays, setGuestPlaysState] = useState(0);
  const [playerState, playerControls] = useAudioPlayer();

  useChannelMeta(channel);

  const loadData = useCallback(async (currentUser: AuthUser | null) => {
    if (!channelId) return;

    setLoading(true);
    setError(null);

    try {
      const [channelData, messagesData] = await Promise.all([
        getChannel(channelId),
        getChannelVoices(channelId),
      ]);

      setChannel(channelData);
      setAccess(messagesData.access);
      setVoices(
        [...messagesData.voices].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        ),
      );

      if (!currentUser) {
        const count = getGuestPlays(channelId);
        setGuestPlaysState(count);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load channel');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      const currentUser = await getMe();
      if (!mounted) return;
      setUser(currentUser);
      if (channelId && !currentUser) {
        setGuestPlaysState(getGuestPlays(channelId));
      }
      await loadData(currentUser);
    };

    bootstrap();

    return () => {
      mounted = false;
      playerControls.reset();
    };
  }, [channelId, loadData]);

  const subscribed = useMemo(
    () => isSubscribed(channel?.subscriptionStatus) || access === 'subscribed',
    [access, channel?.subscriptionStatus],
  );
  const activeVoice = voices.find((voice) => voice.id === playerState.activeVoiceId) || null;
  const hiddenCount = Math.max((channel?.messageCount || 0) - voices.length, 0);

  const openGate = (reason: GateReason) => {
    setLoginReason(reason);
    setLoginError(null);
    setLoginOpen(true);
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      setLoginSubmitting(true);
      setLoginError(null);
      const currentUser = await login(email, password);
      setUser(currentUser);
      setLoginOpen(false);
      if (channelId) {
        setGuestPlays(channelId, 0);
        setGuestPlaysState(0);
      }
      await loadData(currentUser);
    } catch (loginRequestError) {
      setLoginError(loginRequestError instanceof Error ? loginRequestError.message : 'Login failed');
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handlePlay = async (voice: VoiceMessage) => {
    if (!channelId) return;

    if (voice.isLocked) {
      openGate(user ? 'subscriber-only' : 'login');
      return;
    }

    if (!user && guestPlays >= GUEST_PLAY_LIMIT) {
      openGate('guest-limit');
      return;
    }

    if (playerState.activeVoiceId === voice.id) {
      if (playerState.isPlaying) {
        playerControls.pause();
      } else {
        await playerControls.resume();
      }
      return;
    }

    try {
      const playback = await playVoice(voice.id);
      await playerControls.play(voice.id, playback.voice_url || voice.streamUrl);

      if (!user) {
        const nextCount = guestPlays + 1;
        setGuestPlays(channelId, nextCount);
        setGuestPlaysState(nextCount);
      }
    } catch (playError) {
      if (playError instanceof ApiError && playError.status === 403) {
        openGate(user ? 'subscriber-only' : 'login');
        return;
      }
      setError(playError instanceof Error ? playError.message : 'Playback failed');
    }
  };

  if (loading) {
    return (
      <div className="channel-page-loading">
        <div className="channel-page-spinner" />
        <p>Loading channel…</p>
      </div>
    );
  }

  if (error || !channel) {
    return (
      <div className="channel-page-error">
        <h2>Channel unavailable</h2>
        <p>{error || 'Channel not found.'}</p>
      </div>
    );
  }

  return (
    <div className="channel-page">
      <ChannelHeader
        channel={channel}
        isLoggedIn={Boolean(user)}
        isSubscribed={subscribed}
        onLoginClick={() => openGate('login')}
      />

      {!user ? (
        <div className="channel-page-banner">
          <strong>{Math.max(GUEST_PLAY_LIMIT - guestPlays, 0)} guest plays left</strong>
          <span>Login with your email to keep listening after the free preview limit.</span>
        </div>
      ) : null}

      {(access === 'guest_preview' || access === 'member_locked' || access === 'partial') ? (
        <div className="channel-page-banner channel-page-banner--muted">
          <strong>Preview mode</strong>
          <span>Only messages you can access right now are shown here. Login, subscribe, or unlock paid categories in the app for the full feed.</span>
        </div>
      ) : null}

      <VoiceList
        voices={voices}
        playerState={playerState}
        onVoiceToggle={handlePlay}
        hiddenCount={hiddenCount}
        access={access}
        onShowGate={() => openGate(user ? 'subscriber-only' : 'login')}
      />

      <AudioPlayer
        voice={activeVoice}
        state={playerState}
        onToggle={() => {
          if (!activeVoice) return;
          void handlePlay(activeVoice);
        }}
        onSeek={playerControls.seek}
      />

      <LoginGate
        isOpen={loginOpen}
        reason={loginReason}
        isSubmitting={loginSubmitting}
        error={loginError}
        user={user}
        channelName={channel.name}
        onClose={() => setLoginOpen(false)}
        onLogin={handleLogin}
      />
    </div>
  );
}
